import { Grid } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import React, { ChangeEvent, Component, SyntheticEvent } from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'react-recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { push as Push } from 'redux-first-history';
import styled from 'styled-components';

import {
    AccessLevel,
    CompressionFormat,
    Contact,
    ContactsOrderBy,
    ContactStatus,
    ExportFileFormats,
    SuppressedStatus,
} from '@dev/ee-api/ee-api-models';
import {
    ContentLoader,
    CustomTable,
    CustomTableHeadersProps,
    CustomTableRowsProps,
    EmptyState,
    FilterIcon,
    FiltersPopover,
    MobileTable,
    Pagination,
    palette,
    Paper,
    SearchInput,
    SimplePopover as Popover,
    Tag,
    TertiaryButton,
    TertiaryButtonGroup,
    Typography,
} from '@dev/ee-components';
import { convertToLocalTime } from '@dev/ee-tools';
import { flatten, isEmpty } from 'lodash-es';

import { ApplicationState as State } from '@api/store';
import SuppressionsProps, { SuppressionState } from './SuppressionsInterface';

import { ExportSuppressionsParams } from '@api/services/ElasticApi';
import dayjs from '@api/services/tools/dayjs';
import {
    AddSuppression,
    CloseSupperssionsScreen,
    DeleteSuppressions,
    ExportSuppression,
    FindSuppressions,
    LoadSuppressions,
} from '@api/store/suppressions/actions';
import { SuppressionPayload } from '@api/store/suppressions/types';

import { DeliveryLogBadge } from '@api/components/DeliveryLogBadge';
import { CustomPopoverBody } from '@api/components/Logs/assets/EventFilter';
import { debounce } from '@api/services/tools/debounce';
import { parseUrlParams } from '@api/services/tools/handleUrlParams';
import { HideDialog, ShowDialog } from '@api/store/dialog/actions';
import { ShowDialogParams } from '@api/store/dialog/types';
import ExportModal from '../../ExportModal';
import DeleteSuppressionModal from '../DeleteSuppressionModal';

const styles = {
    root: {
        padding: '0 16px',
        '& h2': {
            paddingBottom: 8,
        },
        '& .MuiTypography-h1': {
            padding: '16px 15px',
            paddingTop: 0,
        },
        '& .MuiTypography-h2': {
            lineHeight: '24px',
            '& .MuiBox-root': {
                marginBottom: '8px',
            },
        },
        '& .MuiTypography-body1': {
            color: '#677389',
            lineHeight: '20px',
        },
    },
    maxWidthMd: {
        maxWidth: '1270px',
    },

    containerRoot: {
        background: '#fff',
        'margin-top': '2em',
    },

    footerRoot: {
        display: 'flex',
        'align-items': 'center',
        'justify-center': 'space-between',
    },

    deleteButton: {
        border: 'none',
        padding: '0px',
        'font-size': '14px',
        'min-width': 'unset',
        'line-height': '20px',
        'text-transform': 'none',
        '& .MuiButton-label': {
            color: 'red',
        },
    },
    mobileEmptyStatePaper: {
        marginTop: '24px',
        boxShadow: '0px 0px 1px #2D37481A',
        border: `1px solid ${palette.gray[3]}`,
    },
};

const FilterContainer = styled.div`
    padding: 0px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const FilterLabel = styled.span`
    margin: 0;
    & + .MuiChip-root {
        margin-left: 12px;
    }
`;

const SearchInputWrapper = styled.div`
    width: 100%;
    padding: 24px 48px;
`;

const MobileSearchInputWrapper = styled.div`
    display: flex;
    margin-top: 32px;
`;

const MobileWrapper = styled.div`
    & div:last-child {
        & > div {
            & .MuiPagination-root {
                margin-top: 8px;
            }
        }
    }
`;

const IconWrapper = styled.div`
    background: ${palette.basic.paper};
    border: 1px solid ${palette.indigo[6]};
    border-radius: 4px;
    height: 44px;
    width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const MobileTableWrapper = styled.div`
    margin-top: 32px;
`;

const mapState = (state: State, ownProps: SuppressionsProps): SuppressionsProps => {
    return {
        ...ownProps,
        account: state.account,
        browser: state.browser,
        suppressions: state.suppressions,
    };
};

interface DispatchProps {
    LoadSuppressions(params?: SuppressionPayload): void;
    FindSuppressions(params?: SuppressionPayload): void;
    AddSuppression(email: string): void;
    ShowDialog: (params: ShowDialogParams) => void;
    HideDialog: () => void;
    DeleteSuppressions: (emails: string[], selectedAll: boolean) => void;
    ExportSuppression: (params: ExportSuppressionsParams) => void;
    Push: (url: string) => void;
    CloseSupperssionsScreen: () => void;
}
type FilterOptions = { label: string; value: string; count: number };

const dispatchProps = {
    LoadSuppressions,
    FindSuppressions,
    AddSuppression,
    ShowDialog,
    HideDialog,
    DeleteSuppressions,
    ExportSuppression,
    Push,
    CloseSupperssionsScreen,
};

type Props = DispatchProps & SuppressionsProps;

class Suppressions extends Component<Props, SuppressionState> {
    public state: SuppressionState = {
        selectedContacts: [],
        selectedAll: false,
        exportOptions: {
            fileName: null,
            compressionFormat: CompressionFormat.Zip,
            fileFormat: ExportFileFormats.Csv,
        },
    };

    private containerRef = React.createRef<HTMLDivElement>();
    private fileNameRef = React.createRef<HTMLInputElement>();

    public componentDidMount(): void {
        const { LoadSuppressions, history } = this.props;
        const query: any = parseUrlParams(history?.location?.search);
        const params: SuppressionPayload = {};

        if (!isEmpty(query)) {
            if (query.statuses) {
                params.statuses = flatten([query.statuses]);
            }
            if (query.page) {
                params.activePage = parseInt(query.page);
            }

            if (query.search) {
                params.search = query.search;
            }

            if (query.orderBy) {
                params.orderBy = query.orderBy;
            }

            if (query.perPage) {
                params.perPage = parseInt(query.perPage);
            }
        }
        this.setState({ selectedContacts: [] });

        LoadSuppressions(params);
    }

    public componentWillUnmount(): void {
        const { CloseSupperssionsScreen } = this.props;

        CloseSupperssionsScreen();
    }

    private isMobile(): boolean {
        const {
            browser: {
                lessThan: { md },
            },
        } = this.props;

        return md;
    }

    private getPopoverOptions(): FilterOptions[] {
        const { suppressions, t } = this.props;
        const { counts } = suppressions;

        const possibleStatuses = [
            {
                label: t('bounced'),
                value: SuppressedStatus.Bounced,
                count: counts.bouncedCount,
            },
            {
                label: t('unsubscribed'),
                value: SuppressedStatus.Unsubscribed,
                count: counts.unsubscribedCount,
            },
            {
                label: t('complained'),
                value: SuppressedStatus.Complained,
                count: counts.abuseCount,
            },
        ];

        return possibleStatuses.filter((status) => status.count > 0);
    }

    private getCustomPopoverBody(element: FilterOptions): JSX.Element {
        return (
            <FilterContainer>
                <FilterLabel>{element?.label}</FilterLabel>
                <Tag label={element?.count} clickable={false} />
            </FilterContainer>
        );
    }

    private onFilterClick(event: SyntheticEvent<HTMLInputElement>, selectedItems: SuppressedStatus[]): void {
        const { FindSuppressions } = this.props;

        debounce(() => {
            this.setState({ selectedContacts: [], selectedAll: false });
            FindSuppressions({
                statuses: selectedItems,
            });
        }, 500);
    }

    private getFilters(): JSX.Element {
        const { suppressions, t } = this.props;

        // if (this.getPopoverOptions().length === 0) {
        //     return <FilterIcon />;
        // }

        return (
            <Popover
                type="icon"
                options={this.getPopoverOptions()}
                defaultCheckboxValue={suppressions.statuses}
                enableCheckbox
                customDropdownBody={(element: FilterOptions): JSX.Element => (
                    <CustomPopoverBody
                        element={element as { count: number; label: string; value: string }}
                        emptyStateText={t('nostatusesavailable')}
                    />
                )}
                onCheckboxClick={this.onFilterClick.bind(this)}
                popperProps={{
                    disablePortal: false,
                }}
            >
                <FilterIcon />
            </Popover>
        );
    }

    private openDeleteModal(): void {
        const { ShowDialog, t } = this.props;
        const { selectedContacts, selectedAll } = this.state;

        ShowDialog({
            title: selectedAll ? t('deleteallsenders') : t('deletesuppressions_one', { count: selectedContacts.length }),
            body: <DeleteSuppressionModal />,
            cancelLabel: t('cancel'),
            destructiveLabel: t('delete'),
            onClickDestructive: this.removeSuppressions.bind(this),
        });
    }

    removeSuppressions(): void {
        const { DeleteSuppressions } = this.props;
        const { selectedContacts, selectedAll } = this.state;

        DeleteSuppressions(selectedContacts, selectedAll);
        this.setState({ selectedContacts: [], selectedAll: false });
    }

    private getHeaders(): CustomTableHeadersProps[] {
        const { t, suppressions, classes, account } = this.props;
        const { orderBy } = suppressions;
        const { selectedContacts } = this.state;
        const { deleteButton } = classes;

        const canExport = account?.GeneralInfo?.AccountAccessLevel?.includes(AccessLevel.Export);

        if (selectedContacts.length > 0) {
            return [
                {
                    id: 'email',
                    numeric: false,
                    label: `${t('email')}`,
                },
                {
                    id: 'status',
                    numeric: false,
                    label: '',
                },
                {
                    id: 'date',
                    numeric: true,
                    direction: orderBy === ContactsOrderBy.DateAddedAsc ? 'asc' : 'desc',
                    label: '',
                    filterIcon: (
                        <TertiaryButtonGroup>
                            {canExport && (
                                <TertiaryButton
                                    text={t('export')}
                                    onClick={this.openExportSuppressionModal.bind(this)}
                                    id="selectAllButton"
                                />
                            )}
                            <span className={deleteButton}>
                                <TertiaryButton text={t('delete')} onClick={this.openDeleteModal.bind(this)} id="deleteButton" />
                            </span>
                        </TertiaryButtonGroup>
                    ),
                },
            ];
        }

        return [
            {
                id: 'email',
                numeric: false,
                label: t('email'),
            },
            {
                id: 'status',
                numeric: false,
                label: t('status'),
                filterIcon: this.getFilters(),
            },
            {
                id: 'date',
                numeric: true,
                label: t('suppressed'),
                sortable: true,
                direction: orderBy === ContactsOrderBy.DateAddedAsc ? 'asc' : 'desc',
                sortFunction: this.sortByAddDate.bind(this),
            },
        ];
    }

    private sortByAddDate(): void {
        const { LoadSuppressions, suppressions } = this.props;
        let { orderBy } = suppressions;

        if (!orderBy || orderBy === ContactsOrderBy.DateAddedDesc) {
            orderBy = ContactsOrderBy.DateAddedAsc;
        } else {
            orderBy = ContactsOrderBy.DateAddedDesc;
        }

        this.setState({ selectedContacts: [], selectedAll: false });

        LoadSuppressions({ orderBy });
    }

    private getStatusBadge(contactStatus: ContactStatus, index): JSX.Element {
        return <DeliveryLogBadge id={`status-${index}`} status={contactStatus} />;
    }

    private getRows(): CustomTableRowsProps[] {
        const { suppressions } = this.props;
        const { contacts } = suppressions;

        if (isEmpty(contacts)) {
            return [];
        }

        return contacts.map((contact: Contact, index): CustomTableRowsProps => {
            return {
                email: contact.Email,
                status: this.getStatusBadge(contact.Status, index),
                date: [
                    convertToLocalTime(contact.StatusChangeDate || contact.DateAdded, 'L'),
                    dayjs.utc(contact.StatusChangeDate || contact.DateAdded).fromNow(),
                ],
            };
        });
    }

    private getMobileRows(headers: string[]): CustomTableRowsProps[] {
        const {
            suppressions: { contacts, contactsLoading: isLoading },
        } = this.props;

        if (isLoading) {
            const emptyRows = [1, 2, 3];
            return emptyRows.map((el, index) => {
                return headers?.reduce((acc, header) => {
                    return {
                        ...acc,
                        [header]: <ContentLoader height={20} mode={'row'} key={`${index}`} />,
                    };
                }, {});
            });
        }

        if (isEmpty(contacts)) {
            return [];
        }

        return contacts.map((contact: Contact, index): CustomTableRowsProps => {
            return {
                tableTitle: contact.Email,
                status: this.getStatusBadge(contact.Status, index),
                date: (
                    <>
                        <Typography variant="body1" bodyVariant="onPaper" style={{ marginBottom: '2px' }}>
                            {convertToLocalTime(contact.DateAdded, 'L')}
                        </Typography>
                        <Typography variant="body1" bodyVariant="help" style={{ fontSize: '11px' }}>
                            {dayjs.utc(contact.DateAdded).fromNow()}
                        </Typography>
                    </>
                ),
                returnOnItemClick: {
                    email: contact.Email,
                },
            };
        });
    }

    private changeExportFileFormat(data: { value: ExportFileFormats; label: string }): void {
        const { exportOptions } = this.state;
        exportOptions.fileFormat = data.value;

        this.setState({ exportOptions });
    }

    private changeExportCompression(enabled: boolean): void {
        const { exportOptions } = this.state;

        exportOptions.compressionFormat = enabled ? CompressionFormat.Zip : CompressionFormat.None;

        this.setState({ exportOptions });
    }

    private exportSuppressions(): void {
        const { ExportSuppression } = this.props;
        const { selectedContacts, exportOptions, selectedAll } = this.state;

        const fileName = this.fileNameRef.current.value || 'export';
        const fileFormat: ExportFileFormats = exportOptions.fileFormat;

        const exportParams = {
            fileName,
            fileFormat,
            emails: selectedContacts,
            compressionFormat: exportOptions.compressionFormat,
            selectedAll,
        };

        ExportSuppression(exportParams);
    }

    private openExportSuppressionModal(): void {
        const { exportOptions } = this.state;
        const { ShowDialog, t } = this.props;

        ShowDialog({
            title: t('export'),
            body: (
                <ExportModal
                    onCompressionChange={this.changeExportCompression.bind(this)}
                    onFileFormatChange={this.changeExportFileFormat.bind(this)}
                    inputRef={this.fileNameRef}
                    inputValue={exportOptions.fileFormat}
                />
            ),
            confirmLabel: t('export'),
            cancelLabel: t('cancel'),
            onClickConfirm: this.exportSuppressions.bind(this),
        });
    }

    private findSuppression(ev: ChangeEvent<HTMLInputElement>): void {
        const { FindSuppressions } = this.props;

        this.setState({ selectedContacts: [], selectedAll: false });
        FindSuppressions({ search: ev?.currentTarget?.value });
    }

    private changePage(activePage: number, perPage: number): void {
        const { LoadSuppressions } = this.props;

        this.setState({ selectedContacts: [], selectedAll: false });
        LoadSuppressions({ activePage, perPage });
    }

    private onSelectContact(event: SyntheticEvent<HTMLInputElement>, selectedContacts: string[], selectAll: boolean): void {
        this.setState({ selectedContacts, selectedAll: selectAll });
    }

    private handleApllyOnMobileFilter(valuesFromFilters): void {
        const { FindSuppressions } = this.props;
        FindSuppressions({
            statuses: valuesFromFilters[0],
        });
    }

    private removeSingleSuppression(email: string[]): void {
        const { DeleteSuppressions } = this.props;
        DeleteSuppressions(email, false);
    }

    private renderMobileTable(): JSX.Element {
        const { classes, suppressions, t, ShowDialog } = this.props;
        const mobileHeaders = [t('status'), t('suppressed')];
        const mobileRows = this.getMobileRows(mobileHeaders);

        if (!suppressions.contactsLoading && isEmpty(mobileRows)) {
            return (
                <Paper className={classes.mobileEmptyStatePaper} style={{ padding: '32px 0' }}>
                    <EmptyState background={palette.basic.paper} text={t('thereisnodatatodisplay')} />
                </Paper>
            );
        }

        return (
            <MobileTableWrapper>
                <MobileTable
                    rows={mobileRows}
                    headers={mobileHeaders}
                    dropdownData={[
                        {
                            color: palette.red[6],
                            label: 'Delete',
                            value: 'delete',
                        },
                    ]}
                    footer={<></>}
                    onItemClick={(ev, value, mail) => {
                        if (value.value === 'delete') {
                            ShowDialog({
                                title: t('deletesuppressions_one', { count: 1 }),
                                body: <DeleteSuppressionModal />,
                                cancelLabel: t('cancel'),
                                destructiveLabel: t('delete'),
                                onClickDestructive: this.removeSingleSuppression.bind(this, mail.email),
                            });
                        }
                    }}
                />
            </MobileTableWrapper>
        );
    }

    private renderSearch(): JSX.Element {
        const { history, t } = this.props;
        const queryParams = parseUrlParams(history?.location?.search);
        const emailSearch = queryParams?.search || '';

        return (
            <SearchInput
                placeholder={t('searchforsuppression')}
                onChange={this.findSuppression.bind(this)}
                onDelete={this.findSuppression.bind(this)}
                defaultValue={emailSearch}
            />
        );
    }

    private renderMobileSearch(): JSX.Element {
        const { history, t } = this.props;
        const queryParams = parseUrlParams(history?.location?.search);
        const defaultCheckValue = queryParams?.statuses ? flatten([queryParams?.statuses]) : [];

        return (
            <MobileSearchInputWrapper>
                {this.renderSearch()}
                <Grid item style={{ marginLeft: 16 }}>
                    <IconWrapper>
                        <FiltersPopover
                            title={t('status')}
                            onApply={this.handleApllyOnMobileFilter.bind(this)}
                            translations={{ apply: t('apply'), close: t('close') }}
                            tabs={[
                                {
                                    label: t('status') as string,
                                    options: this.getPopoverOptions(),
                                    type: 'checkbox' as any,
                                    defaultCheckboxValue: defaultCheckValue,
                                },
                            ]}
                        >
                            <FilterIcon color={palette.gray[6]} height={20} width={20} />
                        </FiltersPopover>
                    </IconWrapper>
                </Grid>
            </MobileSearchInputWrapper>
        );
    }

    private renderPagination(): JSX.Element {
        const { suppressions } = this.props;
        const { totalCount } = suppressions.counts;
        const isMobile = this.isMobile();

        return (
            <Pagination
                handlePaginationChange={this.changePage.bind(this)}
                activePage={suppressions.activePage}
                perPage={suppressions.perPage}
                dataLength={totalCount}
                disableBorderTop={isMobile}
            />
        );
    }

    public render(): JSX.Element {
        const { classes, suppressions, t } = this.props;
        const { containerRoot } = classes;
        const { totalCount } = suppressions.counts;
        const isMobile = this.isMobile();

        if (isMobile) {
            return (
                <MobileWrapper>
                    {this.renderMobileSearch()}
                    {this.renderMobileTable()}
                    {!!suppressions.contacts.length && (
                        <Paper margin={{ top: 32 }} style={{ width: '100%' }}>
                            {this.renderPagination()}
                        </Paper>
                    )}
                </MobileWrapper>
            );
        }

        return (
            <Paper margin={{ top: 32 }} style={{ width: '100%' }}>
                <Grid className={containerRoot} container>
                    <SearchInputWrapper>{this.renderSearch()}</SearchInputWrapper>
                    <Grid item xs={12}>
                        <CustomTable
                            columnsWidth={{
                                email: '60%',
                            }}
                            onSelectRow={this.onSelectContact.bind(this)}
                            selectedRows={this.state.selectedContacts}
                            headers={this.getHeaders()}
                            rows={this.getRows()}
                            loading={suppressions.contactsLoading}
                            emptyRowsNo={suppressions.perPage}
                            stickyHeader={true}
                            totalCount={totalCount}
                            isEmpty={!suppressions.contactsLoading && !totalCount}
                            emptyStateProps={{ text: t('emptystate') }}
                        />
                    </Grid>
                </Grid>
                {this.renderPagination()}
            </Paper>
        );
    }
}

export default compose(
    withRouter,
    connect<SuppressionsProps, DispatchProps>(mapState, dispatchProps),
    withTranslation(),
    withStyles(styles)
)(Suppressions);
